import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { IIncomesForCashResponse, IExpensesForCashResponse } from '../../apis/models/transactions';

function groupBy<T>(arr: T[], key: (item: T) => string): Map<string, T[]> {
  return arr.reduce((map, item) => {
    const k = key(item);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(item);
    return map;
  }, new Map<string, T[]>());
}

const fmt$ = (n: number) => `$${n.toFixed(2)}`;

const BLUE: [number, number, number] = [25, 118, 210];
const BLUE_DARK: [number, number, number] = [21, 101, 192];
const BLUE_BG: [number, number, number] = [227, 240, 251];
const HEADER_BG: [number, number, number] = [240, 244, 248];
const TEXT: [number, number, number] = [50, 50, 50];
const FOOT_BG: [number, number, number] = [250, 250, 250];

const PAGE_HEIGHT = 297; // A4 mm
const BOTTOM_MARGIN = 15;
const TOP_MARGIN = 15;
// Minimum space needed before starting a group: header band + col header row + 1 data row
const MIN_GROUP_SPACE = 32;
// Minimum space needed before a section title
const MIN_SECTION_SPACE = 20;

function newPage(doc: jsPDF): number {
  doc.addPage();
  return TOP_MARGIN;
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PAGE_HEIGHT - BOTTOM_MARGIN) {
    return newPage(doc);
  }
  return y;
}

function drawSectionTitle(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLUE);
  doc.text(text, 14, y);
  return y + 8;
}

function drawGroupHeader(doc: jsPDF, label: string, y: number): number {
  doc.setFillColor(...BLUE_BG);
  doc.rect(14, y, 182, 6, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLUE_DARK);
  doc.text(label, 16, y + 4.5);
  return y + 7;
}

export interface CashReportLabels {
  incomesTitle: string;
  expensesTitle: string;
  date: string;
  names: string;
  description: string;
  cashier: string;
  amount: string;
  beneficiary: string;
  authoriser: string;
  subtotal: string;
}

export function downloadCombinedCashReport(
  incomes: IIncomesForCashResponse[],
  expenses: IExpensesForCashResponse[],
  resolveFeeType: (key: string) => string,
  labels: CashReportLabels,
  filename: string
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = TOP_MARGIN;

  const hasIncomes = incomes.length > 0;
  const hasExpenses = expenses.length > 0;

  // ── INCOMES ──────────────────────────────────────────────────────────────
  if (hasIncomes) {
    y = ensureSpace(doc, y, MIN_SECTION_SPACE);
    y = drawSectionTitle(doc, labels.incomesTitle, y);

    const incomeGroups = groupBy(incomes, (r) => r.feeType);
    incomeGroups.forEach((rows, feeType) => {
      const label = resolveFeeType(feeType);
      const subtotal = rows.reduce((s, r) => s + r.fee_amount, 0);

      // Ensure the group header + column header + at least one row fit together
      y = ensureSpace(doc, y, MIN_GROUP_SPACE);
      y = drawGroupHeader(doc, label, y);

      autoTable(doc, {
        startY: y,
        head: [[labels.date, labels.names, labels.description, labels.cashier, labels.amount]],
        body: rows.map((r) => [
          r.date_sold,
          r.names,
          r.description ?? '',
          r.cashier ?? '',
          fmt$(r.fee_amount),
        ]),
        foot: [['', '', '', labels.subtotal, fmt$(subtotal)]],
        headStyles: { fillColor: HEADER_BG, textColor: TEXT, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: TEXT },
        footStyles: { fillColor: FOOT_BG, fontStyle: 'bold', fontSize: 8, textColor: TEXT },
        columnStyles: { 4: { halign: 'right' } },
        margin: { left: 14, right: 14, bottom: BOTTOM_MARGIN },
        theme: 'grid',
        // Repeat column headers on every page if table spans multiple pages
        showHead: 'everyPage',
      });

      y = (doc as any).lastAutoTable.finalY + 6;
    });
  }

  // ── EXPENSES — start on a fresh page only when incomes precede them ────────
  if (hasExpenses) {
    if (hasIncomes) {
      doc.addPage();
      y = TOP_MARGIN;
    }
    y = ensureSpace(doc, y, MIN_SECTION_SPACE);
    y = drawSectionTitle(doc, labels.expensesTitle, y);

    const expenseGroups = groupBy(expenses, (r) => r.feeCode);
    expenseGroups.forEach((rows, feeCode) => {
      const label = resolveFeeType(feeCode);
      const subtotal = rows.reduce((s, r) => s + r.fee_amount, 0);

      y = ensureSpace(doc, y, MIN_GROUP_SPACE);
      y = drawGroupHeader(doc, label, y);

      autoTable(doc, {
        startY: y,
        head: [
          [
            labels.date,
            labels.beneficiary,
            labels.description,
            labels.cashier,
            labels.authoriser,
            labels.amount,
          ],
        ],
        body: rows.map((r) => [
          r.date,
          r.beneficiary_name,
          r.description ?? '',
          r.cashier ?? '',
          r.authorizer ?? '',
          fmt$(r.fee_amount),
        ]),
        foot: [['', '', '', '', labels.subtotal, fmt$(subtotal)]],
        headStyles: { fillColor: HEADER_BG, textColor: TEXT, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: TEXT },
        footStyles: { fillColor: FOOT_BG, fontStyle: 'bold', fontSize: 8, textColor: TEXT },
        columnStyles: { 5: { halign: 'right' } },
        margin: { left: 14, right: 14, bottom: BOTTOM_MARGIN },
        theme: 'grid',
        showHead: 'everyPage',
      });

      y = (doc as any).lastAutoTable.finalY + 6;
    });
  }

  doc.save(filename);
}
